import React from 'react';
import {Grid, Skeleton, Stack, IconButton} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {ADMIN_ROLE, PROJECT_ADMIN_ROLE} from 'pwm-shared/src/AccessControl';
import {Vendor} from 'pwm-shared';
import {PWMCard, PWMCardContent, PWMCardHeader} from 'src/PWM/components/PWMCard';
import {PWMPropertyList, PWMPropertyListItem} from 'src/PWM/components/PWMPropertyList';
import {PWMTextBanner} from 'src/PWM/components/PWMTextBanner';
import {BodyText, SubText} from 'src/PWM/components/PWMTypography';
import ConfirmationDialog from 'src/PWM/components/ConfirmationDialog';
import {formatDate} from 'src/PWM/utils/date-utils';
import {tokens} from 'src/PWM/locales/tokens';
import VendorProjectAssignments from 'src/PWM/components/pages/vendorDetail/VendorProjectAssignments';
import RoleGuard from 'src/PWM/guards/role-guard';
import {PWMEditIcon} from 'src/PWM/components/PWMIcons';
import VendorValidations from 'src/PWM/components/pages/vendorDetail/VendorValidations';
import VendorEditInfoComponent from './VendorEditInfoComponent';

const VendorInfoComponent: React.FC<IVendorInfoComponentProps> = (props) => {
  const {data, setIsLoading} = props;

  const [confirmationDialogOpen, setConfirmationDialogOpen] = React.useState<boolean>(false);
  const [confirmationDialogMessage, setConfirmationDialogMessage] = React.useState<string>('');
  const [isEditingVendorInfo, setIsEditingVendorInfo] = React.useState<boolean>(false);
  
  const {t} = useTranslation();

  if (!data) return <Skeleton variant="rectangular" width={400} height={300} />;

  const {
    name,
    address1 = '',
    address2 = '',
    address3 = '',
    city = '',
    state = '',
    zip = '',
    emailAddress = '',
    isActive,
    dirRegNo,
    licNo,
    classifications,
    inActiveMessage,
    inActiveDate,
  } = data;

  const showConfirmationDialog = (message: string, regNo: string) => {
    setConfirmationDialogMessage(`${message} ${t(tokens.interface.backgroundrefresh)}`);
    setConfirmationDialogOpen(true);
  };
  const closeDialog = () => setConfirmationDialogOpen(false);

  const setEditingVendorInfo = () => setIsEditingVendorInfo(true);
  const cancelEditingVendorInfo = () => setIsEditingVendorInfo(false);
  const doneEditingVendorInfo = async (didUpdate: boolean = false) => {
    if (didUpdate) await props.refreshInfo();
    setIsEditingVendorInfo(false);
  };

  return (
    <>
    <Stack spacing={2.5}>
      <PWMCard>
        <PWMCardHeader
          title={name}
          action={
            <RoleGuard allowedRoles={() => [ADMIN_ROLE, PROJECT_ADMIN_ROLE]}>
              <IconButton onClick={setEditingVendorInfo}><PWMEditIcon /></IconButton>
            </RoleGuard>
          }
        />
        <PWMCardContent>
          <Grid container={true}>
            {!isActive && (
              <PWMTextBanner variant="info" text={`**${t(tokens.interface.inactive)} ${t(tokens.interface.vendor)}**`.toUpperCase()} />
            )}
            {data.id && (
              <RoleGuard allowedRoles={() => [ADMIN_ROLE, PROJECT_ADMIN_ROLE]}>
                {isEditingVendorInfo && <VendorEditInfoComponent data={data} done={doneEditingVendorInfo} />}
              </RoleGuard>
            )}
            {!isEditingVendorInfo && (
              <>
                <Grid item={true} xs={12} md={6}>
                  <PWMPropertyList>
                    <PWMPropertyListItem
                      responsive={true}
                      divider={true}
                      label={t(tokens.interface.contactemail)}
                      value={emailAddress}
                    />
                    <PWMPropertyListItem
                      responsive={true}
                      divider={true}
                      label={t(tokens.interface.address)}
                    >
                      {[address1, address2, address3].filter(Boolean).map((text, i) =>
                        <SubText key={`${text}-${i}`}>{text}</SubText>)}
                      <SubText>{city}, {state} {zip}</SubText>
                    </PWMPropertyListItem>
                  </PWMPropertyList>
                </Grid>
                <Grid item={true} xs={12} md={6}>
                  <PWMPropertyList>
                    <PWMPropertyListItem
                      responsive={true}
                      divider={true}
                      small={true}
                      label={t(tokens.interface.dirno)}
                    >
                      <SubText>{dirRegNo}</SubText>
                    </PWMPropertyListItem>
                    {licNo && <PWMPropertyListItem
                      responsive={true}
                      divider={true}
                      small={true}
                      label={t(tokens.interface.licno)}
                    >
                      <SubText>{licNo}</SubText>
                    </PWMPropertyListItem>}
                  </PWMPropertyList>
                </Grid>
              </>
            )}
        </Grid>
      </PWMCardContent>
    </PWMCard>
    {classifications && <PWMCard>
      <PWMCardHeader title={t(tokens.interface.pwcclassification)} />
      <PWMCardContent>
        <BodyText>{classifications.map(({classification}) => classification.name).join(', ')}</BodyText>
      </PWMCardContent>
    </PWMCard>}
    {inActiveDate && (
      <PWMCard>
        <PWMCardHeader
          title={`${t(tokens.interface.projectvendor)} ${t(tokens.interface.inactive)}`}
          subheader={`${t(tokens.interface.projectvendorinactivemessage)}`}
        />
        <PWMCardContent>
          <PWMPropertyList>
            <PWMPropertyListItem
              label={`${t(tokens.interface.date)}`}
              value={formatDate(inActiveDate)}
            />
            <PWMPropertyListItem
              align={'horizontal'}
              label={`${t(tokens.interface.message)}`}
              value={inActiveMessage}
            />
          </PWMPropertyList>
        </PWMCardContent>
      </PWMCard>
    )}
      <VendorValidations
        vendorId={data.id}
        data={data as unknown as any}
        showConfirmationDialog={showConfirmationDialog}
        setIsLoading={setIsLoading}
      />
      <VendorProjectAssignments vendorId={data.id}/>
    </Stack>
  <ConfirmationDialog
    message={confirmationDialogMessage}
    title="Refresh"
    isOpen={confirmationDialogOpen}
    onClose={closeDialog}
  />
</>
)
};

export default VendorInfoComponent;

interface IVendorInfoComponentProps {
  children?: React.ReactNode;
  data: Vendor.ProjectVendorListInfo;
  setIsLoading(isLoading: boolean): any;
  refreshInfo(): Promise<any>;
}